// src/components/dashboard/Claims.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  TablePagination,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CleaningServicesRoundedIcon from "@mui/icons-material/CleaningServicesRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const Claims = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Filter States
  const [patientName, setPatientName] = useState("");
  const [payerName, setPayerName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusStartDate, setStatusStartDate] = useState("");
  const [statusEndDate, setStatusEndDate] = useState("");
  const [status, setStatus] = useState("Pending");
  const [allClaims, setAllClaims] = useState(false);

  // Table States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);

  // Clinical Unified Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";

  // Clinical Claims Dataset
  const [claimsData, setClaimsData] = useState([
    {
      id: "17",
      patientName: "Alexander Wright",
      payerName: "Good Health Insurance Corp",
      policyNo: "GH-1234",
      providerName: "Dr. Usman",
      appointmentDate: "07/14/2026",
      dos: "07/14/2026",
      pos: "11 - Office / Clinic",
      payment: "$450.00",
      status: "Paid",
    },
    {
      id: "16",
      patientName: "Charlotte Bennett",
      payerName: "Aetna Global Health",
      policyNo: "AET-8891",
      providerName: "Dr. Usman",
      appointmentDate: "07/29/2026",
      dos: "07/29/2026",
      pos: "02 - Telehealth Provided Other",
      payment: "$620.00",
      status: "Pending",
    },
    {
      id: "13",
      patientName: "Lucas Miller",
      payerName: "UnitedHealthcare Choice",
      policyNo: "UHC-9902",
      providerName: "Dr. Sarah Jenkins",
      appointmentDate: "07/23/2026",
      dos: "07/23/2026",
      pos: "11 - Office / Clinic",
      payment: "$710.00",
      status: "Paid",
    },
    {
      id: "12",
      patientName: "Sophia Anderson",
      payerName: "Cigna Commercial Care",
      policyNo: "CGN-4512",
      providerName: "Dr. Usman",
      appointmentDate: "07/23/2026",
      dos: "07/23/2026",
      pos: "02 - Telehealth Provided Other",
      payment: "$0.00",
      status: "Rejected",
    },
    {
      id: "11",
      patientName: "Oliver Davies",
      payerName: "Blue Cross Blue Shield",
      policyNo: "BCBS-4343",
      providerName: "Dr. Sarah Jenkins",
      appointmentDate: "07/21/2026",
      dos: "07/21/2026",
      pos: "02 - Telehealth Provided Other",
      payment: "$320.00",
      status: "Pending",
    },
    {
      id: "10",
      patientName: "Emma Taylor",
      payerName: "Humana Medicare Advantage",
      policyNo: "HUM-7734",
      providerName: "Dr. Usman",
      appointmentDate: "07/19/2026",
      dos: "07/19/2026",
      pos: "11 - Office / Clinic",
      payment: "$510.00",
      status: "Paid",
    },
    {
      id: "09",
      patientName: "Benjamin Hayes",
      payerName: "Good Health Insurance Corp",
      policyNo: "GH-8901",
      providerName: "Dr. Usman",
      appointmentDate: "07/16/2026",
      dos: "07/16/2026",
      pos: "11 - Office / Clinic",
      payment: "$0.00",
      status: "Pending",
    },
  ]);

  const payerOptions = [
    "Good Health Insurance Corp",
    "Blue Cross Blue Shield",
    "UnitedHealthcare Choice",
    "Aetna Global Health",
    "Cigna Commercial Care",
    "Humana Medicare Advantage",
  ];

  const statusOptions = ["All", "Pending", "Submitted", "Paid", "Rejected", "Appealed"];

  const handleClear = () => {
    setPatientName("");
    setPayerName("");
    setFromDate("");
    setToDate("");
    setStatusStartDate("");
    setStatusEndDate("");
    setStatus("");
    setAllClaims(false);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(claimsData.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDelete = (id) => {
    setClaimsData((prev) => prev.filter((row) => row.id !== id));
  };

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: controlBg,
      fontSize: "0.85rem",
      color: primaryTextColor,
      colorScheme: isDark ? "dark" : "light",
      "& fieldset": { borderColor: controlBorder },
      "&:hover fieldset": { borderColor: tealMain },
      "&.Mui-focused fieldset": { borderColor: tealMain },
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        color: primaryTextColor,
        borderRadius: "8px",
        boxShadow: isDark ? "0 12px 30px rgba(0,0,0,0.5)" : "0 8px 24px rgba(15,23,42,0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          "&:hover": {
            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
            "&:hover": {
              backgroundColor: isDark ? "rgba(30, 94, 95, 0.45)" : "#cbe2e7",
            },
          },
        },
      },
    },
  };

  const filteredClaims = claimsData.filter((row) => {
    const matchName = patientName
      ? row.patientName.toLowerCase().includes(patientName.toLowerCase())
      : true;
    const matchPayer = payerName ? row.payerName === payerName : true;
    const matchStatus =
      status && status !== "All" ? row.status.toLowerCase() === status.toLowerCase() : true;
    return matchName && matchPayer && matchStatus;
  });

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & ADD CLAIM BUTTON */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.35rem",
            fontWeight: 800,
            color: primaryTextColor,
            letterSpacing: "-0.2px",
          }}
        >
          Claim
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddRoundedIcon sx={{ fontSize: "1.2rem !important" }} />}
          sx={{
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.82rem",
            borderRadius: "6px",
            px: 2.5,
            py: 0.8,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#046337",
              boxShadow: "none",
            },
          }}
        >
          Add
        </Button>
      </Box>

      {/* 2. TOP CLAIMS FILTER CONSOLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: 3 },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3.5,
        }}
      >
        {/* Form Inputs Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            columnGap: 2.5,
            rowGap: 2.2,
          }}
        >
          {/* Patient Name */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              First Name / Patient Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter First Name"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              sx={fieldStyles}
            />
          </Box>

          {/* Payer Name */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              Payer Name
            </Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                      Select Payer...
                    </Typography>
                  );
                }
                return selected;
              }}
              sx={{
                borderRadius: "6px",
                backgroundColor: controlBg,
                fontSize: "0.85rem",
                color: primaryTextColor,
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              {payerOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* From Date */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              From Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              sx={fieldStyles}
            />
          </Box>

          {/* To Date */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              To Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              sx={fieldStyles}
            />
          </Box>

          {/* Status Start Date */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              Status Start Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={statusStartDate}
              onChange={(e) => setStatusStartDate(e.target.value)}
              sx={fieldStyles}
            />
          </Box>

          {/* Status End Date */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              Status End Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={statusEndDate}
              onChange={(e) => setStatusEndDate(e.target.value)}
              sx={fieldStyles}
            />
          </Box>

          {/* Status Select */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
              Status
            </Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              endAdornment={
                status ? (
                  <InputAdornment position="end" sx={{ mr: 1.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setStatus("");
                      }}
                      sx={{ p: 0.2, color: secondaryTextColor }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                      Select Status...
                    </Typography>
                  );
                }
                return (
                  <Chip
                    label={selected}
                    size="small"
                    sx={{
                      height: "22px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: pastelTealBg,
                      color: tealButton,
                      borderRadius: "6px",
                    }}
                  />
                );
              }}
              sx={{
                borderRadius: "6px",
                backgroundColor: controlBg,
                fontSize: "0.85rem",
                color: primaryTextColor,
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              {statusOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* All Claims Checkbox */}
          <Box sx={{ display: "flex", alignItems: "flex-end", pb: 0.4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allClaims}
                  onChange={(e) => setAllClaims(e.target.checked)}
                  size="small"
                  sx={{
                    color: secondaryTextColor,
                    "&.Mui-checked": { color: tealMain },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal }}>
                  All Claims
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* Action Buttons Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1.5,
            pt: 2.5,
            mt: 2.5,
            borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "#F1F5F9"}`,
          }}
        >
          {/* Generate Claim */}
          <Button
            variant="contained"
            startIcon={<CloudUploadRoundedIcon />}
            sx={{
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.4,
              py: 0.9,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1c4a50", boxShadow: "none" },
            }}
          >
            Generate Claim
          </Button>

          {/* Search Button */}
          <Button
            variant="contained"
            startIcon={<SearchRoundedIcon />}
            sx={{
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.6,
              py: 0.9,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1c4a50", boxShadow: "none" },
            }}
          >
            Search
          </Button>

          {/* Clear Button */}
          <Button
            variant="outlined"
            startIcon={<CleaningServicesRoundedIcon />}
            onClick={handleClear}
            sx={{
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.4,
              py: 0.9,
              textTransform: "uppercase",
              "&:hover": {
                borderColor: tealMain,
                backgroundColor: "rgba(36, 94, 101, 0.04)",
              },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. LOWER CLAIMS ROSTER TABLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#EAEFF4"}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "600px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    backgroundColor: isDark ? "#162238" : "#F4F9F9",
                    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#EAEFF4"}`,
                    py: 1.5,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < filteredClaims.length
                    }
                    checked={
                      filteredClaims.length > 0 && selectedIds.length === filteredClaims.length
                    }
                    onChange={handleSelectAll}
                    sx={{
                      color: secondaryTextColor,
                      "&.Mui-checked": { color: tealMain },
                    }}
                  />
                </TableCell>
                <TableCell>Account No</TableCell>
                <TableCell>Patient Name</TableCell>
                <TableCell>Payer Name</TableCell>
                <TableCell>Policy No</TableCell>
                <TableCell>Provider Name</TableCell>
                <TableCell>Appointment Date</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>POS</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredClaims
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const isSelected = selectedIds.includes(row.id);

                  // Status Chip Styling
                  let chipBg = pastelTealBg;
                  let chipColor = tealButton;
                  if (row.status === "Paid") {
                    chipBg = isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7";
                    chipColor = isDark ? "#34D399" : "#15803D";
                  } else if (row.status === "Rejected") {
                    chipBg = isDark ? "rgba(239, 68, 68, 0.15)" : "#FEE2E2";
                    chipColor = isDark ? "#F87171" : "#B91C1C";
                  } else if (row.status === "Pending") {
                    chipBg = isDark ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7";
                    chipColor = isDark ? "#FBBF24" : "#B45309";
                  }

                  return (
                    <TableRow
                      key={row.id}
                      selected={isSelected}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 1.4,
                        },
                      }}
                    >
                      {/* Checkbox */}
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleSelectOne(row.id)}
                          sx={{
                            color: secondaryTextColor,
                            "&.Mui-checked": { color: tealMain },
                          }}
                        />
                      </TableCell>

                      {/* Account ID Badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.76rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {row.id}
                        </Box>
                      </TableCell>

                      {/* Patient Name with Initial Avatar */}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              backgroundColor: tealButton,
                              color: "#FFFFFF",
                            }}
                          >
                            {row.patientName[0]}
                          </Avatar>
                          <Typography
                            sx={{
                              fontSize: "0.84rem",
                              fontWeight: 700,
                              color: primaryTextColor,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.patientName}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Payer Name */}
                      <TableCell sx={{ fontSize: "0.82rem", color: secondaryTextColor, fontWeight: 500 }}>
                        {row.payerName}
                      </TableCell>

                      {/* Policy No */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            color: primaryTextColor,
                            fontWeight: 600,
                          }}
                        >
                          {row.policyNo}
                        </Typography>
                      </TableCell>

                      {/* Provider Name */}
                      <TableCell sx={{ fontSize: "0.82rem", color: primaryTextColor, fontWeight: 600 }}>
                        {row.providerName}
                      </TableCell>

                      {/* Appointment Date */}
                      <TableCell sx={{ fontSize: "0.82rem", color: secondaryTextColor }}>
                        {row.appointmentDate}
                      </TableCell>

                      {/* DOS */}
                      <TableCell sx={{ fontSize: "0.82rem", color: secondaryTextColor }}>
                        {row.dos}
                      </TableCell>

                      {/* POS */}
                      <TableCell>
                        <Chip
                          label={row.pos}
                          size="small"
                          sx={{
                            height: "22px",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
                            color: primaryTextColor,
                          }}
                        />
                      </TableCell>

                      {/* Payment */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.84rem",
                            fontWeight: 800,
                            color: row.payment !== "$0.00" ? "#057A44" : secondaryTextColor,
                          }}
                        >
                          {row.payment}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            height: "22px",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            backgroundColor: chipBg,
                            color: chipColor,
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.3,
                          }}
                        >
                          <Tooltip title="View Claim">
                            <IconButton
                              size="small"
                              sx={{
                                color: secondaryTextColor,
                                "&:hover": { color: tealMain },
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Demographics">
                            <IconButton
                              size="small"
                              sx={{
                                color: secondaryTextColor,
                                "&:hover": { color: tealButton },
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Claim">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.id)}
                              sx={{
                                color: "#EF4444",
                                "&:hover": {
                                  backgroundColor: "rgba(239, 68, 68, 0.08)",
                                },
                              }}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 25]}
          component="div"
          count={filteredClaims.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${cardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Claims;